/**
 * Administration > Audit Log (M2 W5b). The HIPAA "who did what when" viewer over the
 * append-only audit table: newest-first, server-paginated (limit/offset, total from meta),
 * with filters for actor email / action / resource id / date range. Read-only. It is non-PHI
 * by design (it records operator identities, action names, and resource IDs only), surfaced
 * with an explicit caption so no one mistakes it for a clinical record.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, RotateCcw } from "lucide-react";
import {
  SkyButton,
  SkyDataGrid,
  SkyInput,
  SkyMessageBar,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { ApiProblem } from "../../lib/api";
import { listAuditLog, type AuditRow } from "../../lib/adminApi";
import { AdminPage, AdminError } from "./adminShared";

const PAGE_SIZE = 25;

interface Committed {
  actorEmail: string;
  action: string;
  resourceId: string;
  from: string; // YYYY-MM-DD
  to: string; // YYYY-MM-DD
}
const EMPTY: Committed = { actorEmail: "", action: "", resourceId: "", from: "", to: "" };

/** Convert a YYYY-MM-DD date input to an ISO instant (start of day for `from`, end for `to`). */
function toIso(day: string, edge: "start" | "end"): string | undefined {
  if (!day) return undefined;
  return edge === "start" ? `${day}T00:00:00.000Z` : `${day}T23:59:59.999Z`;
}

export function AdminAuditLog() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Draft (form) vs committed (applied) filters, so typing does not fire a request per keystroke.
  const [draft, setDraft] = useState<Committed>(EMPTY);
  const [committed, setCommitted] = useState<Committed>(EMPTY);

  // Generation counter so rapid page/filter changes cannot show a stale page's rows: each
  // load is stamped and only the newest one is allowed to apply its result.
  const loadGenRef = useRef(0);

  const load = useCallback(() => {
    const gen = ++loadGenRef.current;
    setLoading(true);
    setError(null);
    listAuditLog({
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      actorEmail: committed.actorEmail.trim() || undefined,
      action: committed.action.trim() || undefined,
      resourceId: committed.resourceId.trim() || undefined,
      from: toIso(committed.from, "start"),
      to: toIso(committed.to, "end"),
    })
      .then(({ data, meta }) => {
        if (gen !== loadGenRef.current) return;
        // Drift guard: collapse a truthy non-array 200 body to [] so it never reaches the grid's .map().
        setRows(Array.isArray(data) ? data : []);
        if (meta?.total != null) setTotal(meta.total);
      })
      .catch((e: unknown) => {
        if (gen !== loadGenRef.current) return;
        setError(e instanceof ApiProblem ? e.problem.title : "Failed to load the audit log.");
        setRows([]);
      })
      .finally(() => {
        if (gen === loadGenRef.current) setLoading(false);
      });
  }, [page, committed]);

  useEffect(() => {
    load();
  }, [load]);

  function apply() {
    setPage(1);
    setCommitted(draft);
  }
  function reset() {
    setPage(1);
    setDraft(EMPTY);
    setCommitted(EMPTY);
  }

  const columns: SkyDataGridColumn<AuditRow>[] = useMemo(
    () => [
      {
        key: "createdAt",
        header: "When",
        width: "16%",
        renderCell: (_v, row) => new Date(row.createdAt).toLocaleString(),
      },
      { key: "action", header: "Action", width: "18%" },
      { key: "actorEmail", header: "Actor", width: "20%" },
      {
        key: "resourceType",
        header: "Resource",
        width: "22%",
        renderCell: (_v, row) =>
          row.resourceType ? (
            <span>
              {row.resourceType}
              {row.resourceId ? ` : ${row.resourceId}` : ""}
            </span>
          ) : (
            <SkyText size={200}>None</SkyText>
          ),
      },
      {
        key: "traceId",
        header: "Trace id",
        width: "24%",
        renderCell: (_v, row) =>
          row.traceId ? (
            <SkyText size={200} font="monospace">
              {row.traceId}
            </SkyText>
          ) : (
            <SkyText size={200}>None</SkyText>
          ),
      },
    ],
    [],
  );

  return (
    <AdminPage
      crumbs={[
        { key: "home", label: "skyCare", to: "/" },
        { key: "admin", label: "Administration" },
        { key: "audit", label: "Audit Log" },
      ]}
      title="Audit Log"
      description="Immutable record of administrative actions, newest first."
      requires={["manageUsers", "manageRoles", "manageTenants", "manageInstances"]}
    >
      <SkyMessageBar
        intent="info"
        title="Non-PHI by design"
        message="This log records operator identities, action names, and resource IDs only. It contains no participant or clinical data."
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-56 max-w-full">
          <SkyInput
            label="Actor email"
            placeholder="operator@org.example"
            value={draft.actorEmail}
            onChange={(e) => setDraft({ ...draft, actorEmail: e.target.value })}
          />
        </div>
        <div className="w-48 max-w-full">
          <SkyInput
            label="Action"
            placeholder="admin.user.create"
            value={draft.action}
            onChange={(e) => setDraft({ ...draft, action: e.target.value })}
          />
        </div>
        <div className="w-56 max-w-full">
          <SkyInput
            label="Resource id"
            placeholder="UUID"
            value={draft.resourceId}
            onChange={(e) => setDraft({ ...draft, resourceId: e.target.value })}
          />
        </div>
        <div className="w-40 max-w-full">
          <SkyInput
            label="From"
            type="date"
            value={draft.from}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
          />
        </div>
        <div className="w-40 max-w-full">
          <SkyInput
            label="To"
            type="date"
            value={draft.to}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })}
          />
        </div>
        <SkyButton appearance="primary" icon={Search} onClick={apply}>
          Apply
        </SkyButton>
        <SkyButton appearance="subtle" icon={RotateCcw} onClick={reset}>
          Reset
        </SkyButton>
      </div>

      {error && <AdminError title="Could not load the audit log" message={error} />}

      <SkyDataGrid<AuditRow>
        data={rows}
        columns={columns}
        getRowId={(r) => r.id}
        loading={loading}
        pagination
        page={page}
        pageSize={PAGE_SIZE}
        totalItems={total}
        onPageChange={setPage}
        emptyMessage="No audit entries match the filter"
      />
    </AdminPage>
  );
}
